import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ClientService } from './client.service';

export interface RoleLookupModel {
  id: number;
  name: string;
  description: string;
}

export interface RolePermissionModel {
  claimId: number;
  permissionFlags: number;
}

export interface ManagedRoleModel extends RoleLookupModel {
  isSystem: boolean;
  concurrencyStamp: string;
  claims: RolePermissionModel[];
}

export interface ClaimDefinitionModel {
  id: number;
  claimType: number;
  name: string;
  description: string;
  permissionFlags: number;
}

export interface RoleManagementModel {
  roles: ManagedRoleModel[];
  claims: ClaimDefinitionModel[];
}

export interface RoleCreatePayload {
  name: string;
  description: string;
}

export interface RoleUpdatePayload extends RoleCreatePayload {
  concurrencyStamp: string;
  claims: RolePermissionModel[];
}

export interface RoleDeleteImpactModel {
  userCount: number;
  workspaceCount: number;
}

@Injectable({ providedIn: 'root' })
export class RoleService {
  private readonly client = inject(ClientService);

  listByAccount(accountId: number): Observable<RoleLookupModel[]> {
    return this.client.get<RoleLookupModel[]>(`account/${accountId}/roles`);
  }

  getManagement(accountId: number): Observable<RoleManagementModel> {
    return this.client.get<RoleManagementModel>(
      `account/${accountId}/role-management`,
    );
  }

  create(
    accountId: number,
    payload: RoleCreatePayload,
  ): Observable<ManagedRoleModel> {
    return this.client.post<ManagedRoleModel, RoleCreatePayload>(
      `account/${accountId}/roles`,
      payload,
    );
  }

  update(
    accountId: number,
    roleId: number,
    payload: RoleUpdatePayload,
  ): Observable<ManagedRoleModel> {
    return this.client.put<ManagedRoleModel, RoleUpdatePayload>(
      `account/${accountId}/roles/${roleId}`,
      payload,
    );
  }

  getDeleteImpact(
    accountId: number,
    roleId: number,
  ): Observable<RoleDeleteImpactModel> {
    return this.client.get<RoleDeleteImpactModel>(
      `account/${accountId}/roles/${roleId}/delete-impact`,
    );
  }

  delete(accountId: number, roleId: number): Observable<{ deleted: boolean }> {
    return this.client.delete<{ deleted: boolean }>(
      `account/${accountId}/roles/${roleId}`,
    );
  }
}
